import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Tooltip,
  useTheme,
  Button,
} from '@mui/material';
import { ChevronsDown } from 'lucide-mui';
import {
  CompactSegment,
  SegmentEditRecord,
  Member,
} from '../../../../../types';
import { RootState } from '../../../../../reducers';
import { restoreSegmentVersion } from '../../../selectedVideoSlice';
import type { AppDispatch } from '../../../../../store';

interface EditHistoryDialogProps {
  open: boolean;
  onClose: () => void;
  segment: CompactSegment;
}

export default function EditHistoryDialog({
  open,
  onClose,
  segment,
}: EditHistoryDialogProps) {
  const theme = useTheme();
  const dispatch = useDispatch<AppDispatch>();
  const [selectedVersionIndex, setSelectedVersionIndex] = useState<
    number | null
  >(null);
  const originalVersionRef = useRef<HTMLDivElement>(null);
  const { editRecords } = useSelector(
    (state: RootState) => state.selectedVideo.transcript
  );
  const teamMembers = useSelector(
    (state: RootState) => state.team.selectedTeam?.teamSpaceMembers
  );
  const currentUser = useSelector((state: RootState) => state.settings.user);

  const getEditorName = (editorId?: string) => {
    if (!editorId) return 'Unknown user';

    // Check if it's the current user
    if (currentUser?._id === editorId) {
      return currentUser.name || currentUser.email || 'Unknown user';
    }

    // Check team members
    const teamMember = teamMembers?.find(
      (member: Member) => member._id === editorId
    );
    if (teamMember) {
      return teamMember.name || teamMember.email || 'Unknown user';
    }

    return 'Unknown user';
  };

  const scrollToOriginal = () => {
    originalVersionRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (open) {
      setSelectedVersionIndex(null);
    }
  }, [open]);

  const editHistory = useMemo(
    () =>
      segment.history
        ?.map((id) => editRecords?.[id])
        .filter((record): record is SegmentEditRecord => !!record)
        .sort((a, b) => b.timestamp - a.timestamp) || [],
    [segment.history, editRecords]
  );

  const handleRestore = async () => {
    if (selectedVersionIndex === null) return;

    const selectedEdit = editHistory[selectedVersionIndex];
    await dispatch(
      restoreSegmentVersion({
        segmentId: segment.id,
        versionId: selectedEdit.id,
      })
    );
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          height: '80vh',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: theme.palette.background.paper,
        },
      }}
    >
      <DialogTitle>Edit History</DialogTitle>
      <Box sx={{ px: 3, pb: 2 }}>
        <Typography variant="caption" color="primary" gutterBottom>
          Current version
        </Typography>
        <Typography variant="body1">{segment.text}</Typography>
      </Box>
      <DialogContent
        sx={{
          flex: 1,
          overflow: 'auto',
          pt: 0,
        }}
      >
        {editHistory.length > 0 && (
          <>
            <Box
              sx={{
                position: 'sticky',
                top: 0,
                backgroundColor: theme.palette.background.paper,
                pt: 1,
                pb: 1,
                borderBottom: 1,
                borderColor: 'divider',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant="subtitle2" color="text.primary">
                All Previous Versions
              </Typography>
              <Tooltip title="View original version" placement="left">
                <IconButton
                  size="small"
                  onClick={scrollToOriginal}
                  sx={{
                    color: 'primary.main',
                  }}
                >
                  <ChevronsDown fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 1 }}
            >
              {editHistory.map((edit, index, array) => (
                <Box
                  key={edit.id}
                  id={
                    index === array.length - 1 ? 'original-version' : undefined
                  }
                  ref={
                    index === array.length - 1 ? originalVersionRef : undefined
                  }
                  onClick={() => setSelectedVersionIndex(index)}
                  sx={{
                    p: 1,
                    borderRadius: 1,
                    cursor: 'pointer',
                    backgroundColor:
                      selectedVersionIndex === index
                        ? 'action.selected'
                        : 'transparent',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    gutterBottom
                  >
                    {index === array.length - 1
                      ? 'Original Version'
                      : `Previous version #${array.length - 1 - index}`}
                    {edit.edited_by &&
                      ` - Edited by ${getEditorName(edit.edited_by)}`}
                  </Typography>
                  <Typography variant="body1">
                    {edit.previous_state?.[0]?.text || ''}
                  </Typography>
                </Box>
              ))}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose} color="inherit">
          Close
        </Button>
        <Button
          onClick={handleRestore}
          color="primary"
          variant="contained"
          disabled={selectedVersionIndex === null}
        >
          Restore
        </Button>
      </DialogActions>
    </Dialog>
  );
}
